/**
 * Voortgang van gekoppelde kinderen opnieuw ophalen
 * ─────────────────────────────────────────────────
 *
 * Het ouderportaal leest de voortgang uit de lokale store, en die wordt
 * precies één keer gevuld: in de effect op `[session]` in `App.tsx`, dus bij
 * het inloggen. Blijft een ouder daarna ingelogd — op een telefoon eerder
 * regel dan uitzondering — dan bevriest het beeld terwijl het kind op zijn
 * eigen apparaat gewoon doorwerkt.
 *
 * Dat is de vervelendste soort veroudering: er staat data, ze ziet er geldig
 * uit, en niets wijst erop dat ze achterloopt. Een ouder die 's avonds kijkt
 * of er geoefend is, krijgt dan het antwoord van vanmorgen.
 *
 * Deze functie is bewust een losse module en geen prop die door vier lagen
 * heen wordt doorgegeven (`AppContent` → `PortalSwitch` → `ParentShellV2` →
 * pagina). Die keten zou vier bestanden raken voor één knop, en elke laag
 * ertussen zou een parameter dragen die ze zelf niet gebruikt.
 *
 * WAAROM DIT GEEN AUTORISATIE DOET
 *
 * Niets hier controleert of deze ouder aan dit kind gekoppeld is. Dat hoort
 * ook niet: `solo_progress` heeft sinds 0016 een SELECT-policy op
 * `can_view_learner()`, dus de server geeft eenvoudigweg niets terug voor een
 * kind waar je niet bij mag. Een tweede controle hier zou een afwijkende
 * waarheid worden die uit de pas kan gaan lopen met de policy.
 */

import { useStore } from '../../../app/store';
import { createResetState } from '../../../config/initialState';
import { fetchProgressForLearnersOrNull } from '../../../data/supabase/linkedProgress';
import { mergeRemoteIntoLocal } from '../../../data/supabase/soloProgressSync';
export interface RefreshableChild {
  id: string;
  displayName?: string | null;
}
export interface RefreshResult {
  ok: boolean;
  /** Aantal kinderen waarvoor daadwerkelijk een serverstand binnenkwam. */
  updated: number;
}

/**
 * Haalt de serverstand op voor de meegegeven kinderen en voegt die samen met
 * wat er lokaal staat.
 *
 * Levert `ok: false` bij een netwerkfout, zodat de aanroeper dat kan melden.
 * Stil falen is hier extra schadelijk: de ouder drukt op verversen juist
 * omdát hij twijfelt aan wat hij ziet, en een knop die niets zegt bevestigt
 * dan ten onrechte dat het beeld klopt.
 */
export async function refreshChildProgress(children: RefreshableChild[]): Promise<RefreshResult> {
  if (children.length === 0) return {
    ok: true,
    updated: 0
  };
  /*
   ── `ok: false` werkte niet ───────────────────────────────────────────────

   Hier stond een `try/catch` om `fetchProgressForLearners()`. Die functie
   GOOIT niet: ze logt de fout en geeft een lege lijst terug. De catch werd dus
   nooit geraakt, `ok` was in de praktijk altijd `true`, en de verversknop
   meldde "bijgewerkt" terwijl er niets was opgehaald.

   Dat is precies de stille faalstand die de toelichting hierboven belooft te
   voorkomen — de ouder drukt op verversen omdát hij twijfelt, en krijgt een
   bevestiging die niets bevestigt.

   De `OrNull`-variant onderscheidt "mislukt" van "leeg". Die tweede is geen
   fout: een kind dat nog niet geoefend heeft, hoort gewoon nul updates op te
   leveren met `ok: true`.
   */
  const byLearner = await fetchProgressForLearnersOrNull(children.map(c => c.id));
  if (byLearner === null) {
    return {
      ok: false,
      updated: 0
    };
  }
  const state = useStore.getState();
  if (!state.multiAccountState) return {
    ok: false,
    updated: 0
  };
  const learnerData = {
    ...(state.multiAccountState.learnerData ?? {})
  };
  let updated = 0;
  for (const child of children) {
    const remote = byLearner[child.id];
    if (!remote) continue;
    // Dezelfde samenvoeging als bij het kind zelf, zodat er één definitie is
    // van "wat komt van de server". Een kind dat nog nooit op dit apparaat is
    // geweest heeft geen lokale basis; `createResetState` levert die.
    const base = learnerData[child.id] ?? createResetState(child.displayName ?? 'Leerling');
    learnerData[child.id] = mergeRemoteIntoLocal(base as never, remote) as never;
    updated += 1;
  }
  if (updated > 0) {
    useStore.setState({
      multiAccountState: {
        ...state.multiAccountState,
        learnerData
      }
    });
  }
  return {
    ok: true,
    updated
  };
}